import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown, MapPin, Users, Lock, DollarSign } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';

interface FormData {
  // Property Address
  propertyNickname: string;
  street1: string;
  street2: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  propertyType: string;

  // Primary Contact
  title: string;
  firstName: string;
  lastName: string;
  companyName: string;

  // Access & Site Information
  accessInstructions: string;
  keyCollectionDetails: string;
  parkingAvailable: boolean;
  parkingDetails: string;

  // Billing Contact
  billingContactDifferent: boolean;
  billingTitle: string;
  billingFirstName: string;
  billingLastName: string;
  billingCompanyName: string;
  billingEmail: string;
}

const RegisterPropertyPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showBillingContact, setShowBillingContact] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    propertyNickname: '',
    street1: '',
    street2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    propertyType: '',

    title: 'Mr.',
    firstName: '',
    lastName: '',
    companyName: '',

    accessInstructions: '',
    keyCollectionDetails: '',
    parkingAvailable: false,
    parkingDetails: '',

    billingContactDifferent: false,
    billingTitle: 'Mr.',
    billingFirstName: '',
    billingLastName: '',
    billingCompanyName: '',
    billingEmail: '',
  });

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.propertyNickname || !formData.street1 || !formData.city || !formData.postcode) {
      toast({ title: 'Missing details', description: 'Property nickname, street, city, and postcode are required.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiService.request('/properties', 'POST', formData);
      if (response.success) {
        setShowSuccessScreen(true);
      } else {
        toast({ title: 'Failed to add property', description: response.message || 'Please try again.', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({ title: 'Failed to add property', description: error.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (showSuccessScreen) {
    return (
      <>
        <Helmet>
          <title>Property Added | Virewire Property Services</title>
        </Helmet>

        <DashboardLayout>
          <div className="w-full max-w-2xl mx-auto">
            {/* Success Screen */}
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
                <svg
                  className="w-12 h-12 text-[#00543b]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <h1 className="text-3xl font-bold text-[#00543b] mb-3">
                Property added successfully
              </h1>
              <p className="text-gray-600 mb-8">
                We have successfully catalogued your new asset into the Virewire portfolio.
              </p>

              {/* Confirmed Details Card */}
              <Card className="mb-8 border-gray-200">
                <CardContent className="p-6">
                  <div className="flex gap-4 justify-center mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                    <div className="text-left">
                      <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                        CONFIRMED DETAILS
                      </p>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {formData.propertyNickname}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {formData.street1}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formData.city}, {formData.postcode}
                      </p>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                          <span className="w-2 h-2 bg-green-700 rounded-full mr-2"></span>
                          Active in Portfolio
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/dashboard/request-service')}
                  className="bg-[#00543b] hover:bg-[#003d2d] px-8"
                >
                  Request a Service for This Property
                </Button>
                <Button
                  onClick={() => navigate('/properties')}
                  variant="outline"
                  className="border-gray-300 px-8"
                >
                  Add Another Property
                </Button>
              </div>

              <p className="text-gray-600 text-sm mt-8 max-w-xl mx-auto">
                "Our goal is to ensure your properties are managed with the highest level of care. Our team is now standing by to assist with any maintenance or concierge requests."
              </p>
              <p className="text-[#00543b] font-semibold text-sm mt-2">
                — The Virewire Management Team
              </p>

              <Button
                onClick={() => navigate('/properties')}
                variant="ghost"
                className="mt-8 text-[#00543b]"
              >
                ← Back to Properties
              </Button>
            </div>
          </div>
        </DashboardLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register Property | Virewire Property Services</title>
        <meta name="description" content="Register a new property in your Virewire portfolio." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-[#00543b] mb-2">
            Register Property
          </h1>
          <p className="text-gray-600 mb-8">
            We are ready to onboard your new location. Please provide the essential details below to ensure seamless service delivery.
          </p>

          <div className="space-y-6">
            {/* Property Address Section */}
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center gap-3">
                <MapPin className="h-5 w-5 text-[#00543b]" />
                <CardTitle>Property Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    PROPERTY NICKNAME
                  </label>
                  <Input
                    placeholder="e.g. London HQ"
                    value={formData.propertyNickname}
                    onChange={(e) =>
                      updateField('propertyNickname', e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    STREET 1
                  </label>
                  <Input
                    placeholder=""
                    value={formData.street1}
                    onChange={(e) => updateField('street1', e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    STREET 2 (OPTIONAL)
                  </label>
                  <Input
                    placeholder=""
                    value={formData.street2}
                    onChange={(e) => updateField('street2', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      CITY
                    </label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      COUNTY
                    </label>
                    <Input
                      value={formData.county}
                      onChange={(e) => updateField('county', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      POSTCODE
                    </label>
                    <Input
                      value={formData.postcode}
                      onChange={(e) => updateField('postcode', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      COUNTRY
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => updateField('country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option>United Kingdom</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    PROPERTY TYPE
                  </label>
                  <select
                    value={formData.propertyType}
                    onChange={(e) => updateField('propertyType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select type...</option>
                    <option value="residential">Residential</option>
                    <option value="apartment">Apartment</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Primary Contact Section */}
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center gap-3">
                <Users className="h-5 w-5 text-[#00543b]" />
                <CardTitle>Primary Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      TITLE
                    </label>
                    <select
                      value={formData.title}
                      onChange={(e) => updateField('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option>Mr.</option>
                      <option>Mrs.</option>
                      <option>Ms.</option>
                      <option>Dr.</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      FIRST NAME
                    </label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => updateField('firstName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      LAST NAME
                    </label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => updateField('lastName', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    COMPANY NAME (OPTIONAL)
                  </label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => updateField('companyName', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Access & Site Information */}
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center gap-3">
                <Lock className="h-5 w-5 text-[#00543b]" />
                <CardTitle>Access & Site Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    ACCESS INSTRUCTIONS
                  </label>
                  <textarea
                    placeholder="Describe how we can enter the premises..."
                    value={formData.accessInstructions}
                    onChange={(e) =>
                      updateField('accessInstructions', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none h-24"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-2">
                    KEY COLLECTION DETAILS
                  </label>
                  <textarea
                    placeholder="Where are the keys located?"
                    value={formData.keyCollectionDetails}
                    onChange={(e) =>
                      updateField('keyCollectionDetails', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none h-20"
                  />
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <button
                    onClick={() =>
                      updateField(
                        'parkingAvailable',
                        !formData.parkingAvailable
                      )
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors mt-1 flex-shrink-0 ${
                      formData.parkingAvailable
                        ? 'bg-[#00543b]'
                        : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.parkingAvailable ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div>
                    <p className="font-semibold text-gray-900">Parking Available</p>
                    <p className="text-sm text-gray-600">
                      Toggle if there is on-site parking for technicians
                    </p>
                  </div>
                </div>

                {formData.parkingAvailable && (
                  <div>
                    <label className="text-sm font-semibold text-gray-900 block mb-2">
                      PARKING DETAILS
                    </label>
                    <Input
                      placeholder="Space number, height restrictions, etc."
                      value={formData.parkingDetails}
                      onChange={(e) =>
                        updateField('parkingDetails', e.target.value)
                      }
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing Contact Section */}
            <Card className="border-gray-200">
              <button
                onClick={() => setShowBillingContact(!showBillingContact)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-[#00543b]" />
                  <CardTitle>Billing Contact</CardTitle>
                  <span className="text-xs font-semibold text-gray-500 uppercase">
                    Optional
                  </span>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-gray-600 transition-transform ${
                    showBillingContact ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {showBillingContact && (
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.billingContactDifferent}
                      onChange={(e) =>
                        updateField(
                          'billingContactDifferent',
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label className="text-sm text-gray-700">
                      Only provide if different from the Primary Contact.
                    </label>
                  </div>

                  {formData.billingContactDifferent && (
                    <>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-900 block mb-2">
                            TITLE
                          </label>
                          <select
                            value={formData.billingTitle}
                            onChange={(e) =>
                              updateField('billingTitle', e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            <option>Mr.</option>
                            <option>Mrs.</option>
                            <option>Ms.</option>
                            <option>Dr.</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-900 block mb-2">
                            FIRST NAME
                          </label>
                          <Input
                            value={formData.billingFirstName}
                            onChange={(e) =>
                              updateField(
                                'billingFirstName',
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-900 block mb-2">
                            LAST NAME
                          </label>
                          <Input
                            value={formData.billingLastName}
                            onChange={(e) =>
                              updateField('billingLastName', e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-900 block mb-2">
                            COMPANY NAME (OPTIONAL)
                          </label>
                          <Input
                            value={formData.billingCompanyName}
                            onChange={(e) =>
                              updateField(
                                'billingCompanyName',
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-gray-900 block mb-2">
                            EMAIL ADDRESS
                          </label>
                          <Input
                            type="email"
                            value={formData.billingEmail}
                            onChange={(e) =>
                              updateField('billingEmail', e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <Button
                variant="outline"
                onClick={() => navigate('/properties')}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#00543b] hover:bg-[#003d2d]"
              >
                {submitting ? 'Adding...' : 'Add Property'}
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default RegisterPropertyPage;
